"use client";
import type {
  AuditImageCardProps,
  AuditImageSectionProps,
} from "../../../types/audit-image";
import type {
  AuditFormProps,
  SessionProgress,
  SetSessionProgress,
} from "../../../types/audit-form";

import type { AuditFileMetadata } from "../../../types/audit-file";
import { useLatest } from "../../../hooks/use-latest";
import { useImageDescriptions } from "../../../hooks/use-image-descriptions";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../../utils/mutationResult.js";

import {
  AlertCircle,
  AlertTriangle,
  Building,
  Cable,
  Camera,
  Check,
  ChevronDown,
  ChevronUp,
  Eye,
  HardHat,
  Loader2,
  Plus,
  Save,
  Server,
  Trash2,
  Wrench,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { LoadingIndicator } from "../../../components/ui/loading-indicator";
import { useDebouncedSave } from "../../../hooks/use-debounced-save";
import { useDraftAutosave } from "../../../hooks/use-draft-autosave";
import { useStableMemoComponent } from "../../../hooks/use-stable-memo-component";
import {
  useVersionAwareFiles,
  useVersionAwareObservations,
  useVersionAwareSiteData,
} from "../../../hooks/use-version-aware-data";
import {
  OptimizedCheckbox,
  OptimizedInput,
  OptimizedSelect,
  OptimizedTextarea,
} from ".././optimized-form-field";

import { useNotification } from "../../../hooks/useNotification";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  deleteFile,
  fetchFiles,
  updateFile,
  uploadMultipleFiles,
} from "../../../redux/slices/fileSlice.js";
import {
  createObservation,
  deleteObservation,
  fetchObservations,
  updateObservation,
} from "../../../redux/slices/observationSlice.js";
import { updateSiteData } from "../../../redux/slices/siteDataSlice.js";
import { operationalConstructionAspectsProgress } from "../../../utils/componentUtils.js";
import { notificationHelpers } from "../../../utils/notificationHelpers";
import { OfflineImage } from "../../shared/offline-image";
import { ImageCapturePicker } from "../shared/image-capture-picker";
import { LocalSectionImportControl } from "../shared/local-section-import-control";

interface ConstructionAspectsFormProps extends AuditFormProps {
  siteId: string;
  templateId: string;
  reportId: string;
  readOnly?: boolean;
  isViewingHistoricalVersion?: boolean;
  versionData?: any;
  projectId: string;
  reportTemplate: string;
  sessionProgress: SessionProgress;
  setSessionProgress: SetSessionProgress;
  previousCompletedReports?: any[];
}

interface ConstructionImage extends AuditFileMetadata {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  description: string;
  uploadDate: Date;
}

interface ObservationItem {
  id: string;
  title: string;
  category:
    | "civil"
    | "mechanical"
    | "electrical"
    | "module_cable"
    | "general"
    | "inverter";
  description: string;
  status: "ok" | "not-ok" | "n-a" | "pending";
  comments: string;
  marksAsPunchListItem: boolean;
  observationDate: string;
  images: ConstructionImage[];
}

// MAIN COMBINED CONSTRUCTION DATA STRUCTURE
interface ConstructionData {
  // ========== FROM FIRST COMPONENT ==========
  // Module Installation
  moduleInstallationCompliant: boolean;
  moduleInstallationCompliantComment: string;
  moduleInstallationNotes: string;
  moduleClampingCompliant: boolean;
  moduleClampingCompliantComment: string;
  moduleClampingNotes: string;

  // Structure Alignment
  structureAlignmentCompliant: boolean;
  structureAlignmentCompliantComment: string;
  structureMisalignmentDetails: string;
  mechanicalStressConcerns: string;

  // Cable Management System
  cableBendingRadiusCompliant: boolean;
  cableBendingRadiusCompliantComment: string;
  cableTensionCompliant: boolean;
  cableTensionCompliantComment: string;
  cableManagementNotes: string;

  // Cable Installation (formerly Cable Laying)
  cableInstallationCompliant: boolean;
  cableInstallationCompliantComment: string;
  cableSupportCompliant: boolean;
  cableSupportCompliantComment: string;
  mc4ConnectorSupportCompliant: boolean;
  mc4ConnectorSupportCompliantComment: string;
  cableSupportNotes: string;

  // Mechanical Protection
  cableProtectionCompliant: boolean;
  cableProtectionCompliantComment: string;
  sharpEdgeProtectionCompliant: boolean;
  sharpEdgeProtectionCompliantComment: string;
  protectionIssuesDetails: string;

  // Cable String Numbering
  cableNumberingCompliant: boolean;
  cableNumberingCompliantComment: string;
  numberingSystemDescription: string;
  numberingIssuesDetails: string;

  // Cable Issues
  cableIssuesRodentNests: boolean;
  cableIssuesRodentNestsComment: string;
  cableIssuesDamagedCable: boolean;
  cableIssuesDamagedCableComment: string;
  cableIssuesWrongCableSize: boolean;
  cableIssuesWrongCableSizeComment: string;
  cableIssuesTooManyRepairedJoints: boolean;
  cableIssuesTooManyRepairedJointsComment: string;
  cableIssuesAdditionalNotes: string;

  // ========== FROM SECOND COMPONENT ==========
  siteObservations: string;

  // Civil Section
  civil_conditionOfSiteAccessRoad: string;
  civil_constructionCompoundCondition: string;
  civil_trafficProblemsForResidents: string;
  civil_separateOffloadingArea: boolean;
  civil_separateOffloadingAreaComment: string;
  civil_onsiteTrafficManagement: string;
  civil_vehiclesSuitableForSite: boolean;
  civil_vehiclesSuitableForSiteComment: string;
  civil_lvTrenchCondition: string;
  civil_hvTrenchCondition: string;
  civil_groundConditionProblems: string;
  civil_civilNotes: string;

  // Mechanical Section - Framework
  mechanical_pilingQuality: string;
  mechanical_mountingAngleAdjustment: string;
  mechanical_rammingHeadProfile: string;
  mechanical_rammingDepth: string;
  mechanical_undergroundSurprises: string;
  mechanical_alignmentTwisted: string;
  mechanical_regalvanizationMethod: string;
  mechanical_drillingConcrete: string;
  mechanical_averageRemainingTime: string;
  mechanical_separateOffloadingArea: boolean;
  mechanical_separateOffloadingAreaComment: string;
  mechanical_vehiclesSuitableForSite: boolean;
  mechanical_vehiclesSuitableForSiteComment: string;
  mechanical_onsiteTrafficManagement: string;
  mechanical_lvTrenchCondition: string;
  mechanical_hvTrenchCondition: string;
  mechanical_groundConditionProblems: string;
  mechanical_mechanicalNotes: string;

  // ========== ELECTRICAL SECTION ==========
  electrical_stringCableType: string;
  electrical_noAboveGroundCable: boolean;
  electrical_noAboveGroundCableComment: string;
  electrical_noBelowGroundCable: boolean;
  electrical_noBelowGroundCableComment: string;
  electrical_looseCableEnds: boolean;
  electrical_looseCableEndsComment: string;
  electrical_mc4ConnectorSupport: boolean;
  electrical_mc4ConnectorSupportComment: string;
  electrical_cableSupport: boolean;
  electrical_cableSupportComment: string;
  electrical_sharpEdgeExposure: boolean;
  electrical_sharpEdgeExposureComment: string;
  electrical_cableStringNumbering: boolean;
  electrical_cableStringNumberingComment: string;
  electrical_conduitManagement: boolean;
  electrical_conduitManagementComment: string;
  electrical_conduitFilledWithFoam: boolean;
  electrical_conduitFilledWithFoamComment: string;
  electrical_cablesWithoutEndCap: boolean;
  electrical_cablesWithoutEndCapComment: string;
  electrical_cableSpecification: string;
  electrical_cableInstallationMethodology: string;
  electrical_biMetallicCompatibility: boolean;
  electrical_biMetallicCompatibilityComment: string;
  electrical_cableConnectorsCrimpsDies: string;
  electrical_electricalNotes: string;

  // Electrical - String Above Ground
  electrical_stringAboveLooseCableConnector: boolean;
  electrical_stringAboveLooseCableConnectorComment: string;
  electrical_stringAboveProperSupport: boolean;
  electrical_stringAboveProperSupportComment: string;
  electrical_stringAboveNoSharpEdge: boolean;
  electrical_stringAboveNoSharpEdgeComment: string;
  electrical_stringAboveCableNumbering: boolean;
  electrical_stringAboveCableNumberingComment: string;

  // Electrical - String Below Ground
  electrical_stringBelowRoutedThroughConduit: boolean;
  electrical_stringBelowRoutedThroughConduitComment: string;
  electrical_stringBelowConduitSealedWithFoam: boolean;
  electrical_stringBelowConduitSealedWithFoamComment: string;

  // Electrical - DC Main
  electrical_dcMainCablesWithoutEndCap: boolean;
  electrical_dcMainCablesWithoutEndCapComment: string;
  electrical_dcMainCableSpecification: string;
  electrical_dcMainInstallationMethodology: string;
  electrical_dcMainBimetallicCompatibility: boolean;
  electrical_dcMainBimetallicCompatibilityComment: string;
  electrical_dcMainCableJointsCrimpsDies: string;

  // ========== INVERTER SECTION ==========
  inverterCoolingFans: boolean;
  inverterCoolingFansComment: string;
  inverterErrorAlarms: boolean;
  inverterErrorAlarmsComment: string;
  inverterDisplay: boolean;
  inverterDisplayComment: string;
  inverterFilters: boolean;
  inverterFiltersComment: string;
  inverterConnections: boolean;
  inverterConnectionsComment: string;
  inverterCorrosion: boolean;
  inverterCorrosionComment: string;
  inverterVentilation: boolean;
  inverterVentilationComment: string;
  inverterAdditionalNotes: string;

  // ========== GENERAL ==========
  mechanicalProtectionCompliant: boolean;
  cableNumberingSystemDescription: string;
  correctiveActions: string;
  priorityLevel: string;
  constructionImages: ConstructionImage[];
  constructionObservations: ObservationItem[];
  punchListSummary: string;
}

const initialData: ConstructionData = {
  // ========== FROM FIRST COMPONENT ==========
  moduleInstallationCompliant: false,
  moduleInstallationCompliantComment: "",
  moduleInstallationNotes: "",
  moduleClampingCompliant: false,
  moduleClampingCompliantComment: "",
  moduleClampingNotes: "",
  structureAlignmentCompliant: false,
  structureAlignmentCompliantComment: "",
  structureMisalignmentDetails: "",
  mechanicalStressConcerns: "",
  cableBendingRadiusCompliant: false,
  cableBendingRadiusCompliantComment: "",
  cableTensionCompliant: false,
  cableTensionCompliantComment: "",
  cableManagementNotes: "",
  cableInstallationCompliant: false,
  cableInstallationCompliantComment: "",
  cableSupportCompliant: false,
  cableSupportCompliantComment: "",
  mc4ConnectorSupportCompliant: false,
  mc4ConnectorSupportCompliantComment: "",
  cableSupportNotes: "",
  cableProtectionCompliant: false,
  cableProtectionCompliantComment: "",
  sharpEdgeProtectionCompliant: false,
  sharpEdgeProtectionCompliantComment: "",
  protectionIssuesDetails: "",
  cableNumberingCompliant: false,
  cableNumberingCompliantComment: "",
  numberingSystemDescription: "",
  numberingIssuesDetails: "",
  cableIssuesRodentNests: false,
  cableIssuesRodentNestsComment: "",
  cableIssuesDamagedCable: false,
  cableIssuesDamagedCableComment: "",
  cableIssuesWrongCableSize: false,
  cableIssuesWrongCableSizeComment: "",
  cableIssuesTooManyRepairedJoints: false,
  cableIssuesTooManyRepairedJointsComment: "",
  cableIssuesAdditionalNotes: "",

  // ========== FROM SECOND COMPONENT ==========
  siteObservations: "",
  civil_conditionOfSiteAccessRoad: "",
  civil_constructionCompoundCondition: "",
  civil_trafficProblemsForResidents: "",
  civil_separateOffloadingArea: false,
  civil_separateOffloadingAreaComment: "",
  civil_onsiteTrafficManagement: "",
  civil_vehiclesSuitableForSite: false,
  civil_vehiclesSuitableForSiteComment: "",
  civil_lvTrenchCondition: "",
  civil_hvTrenchCondition: "",
  civil_groundConditionProblems: "",
  civil_civilNotes: "",
  mechanical_pilingQuality: "",
  mechanical_mountingAngleAdjustment: "",
  mechanical_rammingHeadProfile: "",
  mechanical_rammingDepth: "",
  mechanical_undergroundSurprises: "",
  mechanical_alignmentTwisted: "",
  mechanical_regalvanizationMethod: "",
  mechanical_drillingConcrete: "",
  mechanical_averageRemainingTime: "",
  mechanical_separateOffloadingArea: false,
  mechanical_separateOffloadingAreaComment: "",
  mechanical_vehiclesSuitableForSite: false,
  mechanical_vehiclesSuitableForSiteComment: "",
  mechanical_onsiteTrafficManagement: "",
  mechanical_lvTrenchCondition: "",
  mechanical_hvTrenchCondition: "",
  mechanical_groundConditionProblems: "",
  mechanical_mechanicalNotes: "",

  // ========== ELECTRICAL SECTION ==========
  electrical_stringCableType: "",
  electrical_noAboveGroundCable: false,
  electrical_noAboveGroundCableComment: "",
  electrical_noBelowGroundCable: false,
  electrical_noBelowGroundCableComment: "",
  electrical_looseCableEnds: false,
  electrical_looseCableEndsComment: "",
  electrical_mc4ConnectorSupport: false,
  electrical_mc4ConnectorSupportComment: "",
  electrical_cableSupport: false,
  electrical_cableSupportComment: "",
  electrical_sharpEdgeExposure: false,
  electrical_sharpEdgeExposureComment: "",
  electrical_cableStringNumbering: false,
  electrical_cableStringNumberingComment: "",
  electrical_conduitManagement: false,
  electrical_conduitManagementComment: "",
  electrical_conduitFilledWithFoam: false,
  electrical_conduitFilledWithFoamComment: "",
  electrical_cablesWithoutEndCap: false,
  electrical_cablesWithoutEndCapComment: "",
  electrical_cableSpecification: "",
  electrical_cableInstallationMethodology: "",
  electrical_biMetallicCompatibility: false,
  electrical_biMetallicCompatibilityComment: "",
  electrical_cableConnectorsCrimpsDies: "",
  electrical_electricalNotes: "",
  electrical_stringAboveLooseCableConnector: false,
  electrical_stringAboveLooseCableConnectorComment: "",
  electrical_stringAboveProperSupport: false,
  electrical_stringAboveProperSupportComment: "",
  electrical_stringAboveNoSharpEdge: false,
  electrical_stringAboveNoSharpEdgeComment: "",
  electrical_stringAboveCableNumbering: false,
  electrical_stringAboveCableNumberingComment: "",
  electrical_stringBelowRoutedThroughConduit: false,
  electrical_stringBelowRoutedThroughConduitComment: "",
  electrical_stringBelowConduitSealedWithFoam: false,
  electrical_stringBelowConduitSealedWithFoamComment: "",
  electrical_dcMainCablesWithoutEndCap: false,
  electrical_dcMainCablesWithoutEndCapComment: "",
  electrical_dcMainCableSpecification: "",
  electrical_dcMainInstallationMethodology: "",
  electrical_dcMainBimetallicCompatibility: false,
  electrical_dcMainBimetallicCompatibilityComment: "",
  electrical_dcMainCableJointsCrimpsDies: "",

  // ========== INVERTER SECTION ==========
  inverterCoolingFans: false,
  inverterCoolingFansComment: "",
  inverterErrorAlarms: false,
  inverterErrorAlarmsComment: "",
  inverterDisplay: false,
  inverterDisplayComment: "",
  inverterFilters: false,
  inverterFiltersComment: "",
  inverterConnections: false,
  inverterConnectionsComment: "",
  inverterCorrosion: false,
  inverterCorrosionComment: "",
  inverterVentilation: false,
  inverterVentilationComment: "",
  inverterAdditionalNotes: "",

  // ========== GENERAL ==========
  mechanicalProtectionCompliant: false,
  cableNumberingSystemDescription: "",
  correctiveActions: "",
  priorityLevel: "medium",
  constructionImages: [],
  constructionObservations: [],
  punchListSummary: "",
};

// Options

const cableTypeOptions = [
  { value: "above_ground", label: "Above Ground" },
  { value: "below_ground", label: "Below Ground" },
  { value: "both", label: "Both Above & Below Ground" },
  { value: "none", label: "No Cables Present" },
];

const pilingQualityOptions = [
  { value: "excellent", label: "Excellent" },
  { value: "good", label: "Good" },
  { value: "fair", label: "Fair" },
  { value: "poor", label: "Poor" },
  { value: "unsatisfactory", label: "Unsatisfactory" },
];

// Predefined observation titles
const PREDEFINED_CIVIL_TITLES = [
  "Condition of Site Access Road",
  "Condition of Construction Compound",
  "Traffic Problems for Local Residents",
  "Onsite Traffic Management",
  "Condition of LV Trench",
  "Condition of HV Trench",
  "Ground Condition Problems",
  "Separate Offloading/Turning Area",
  "Vehicles Suitable for Site Conditions",
];

const PREDEFINED_MECHANICAL_TITLES = [
  "Quality of Piling/Ramming the Post",
  "Mounting Angle Adjustment",
  "Ramming Head Profile Matching",
  "Ramming Depth",
  "Underground Surprises",
  "Alignment / Twisted Issues",
  "Re-galvanization Method",
  "Drilling / Concrete Work",
  "Average Remaining Time",
];

const PREDEFINED_ELECTRICAL_TITLES = [
  "String Cable Management - Above Ground",
  "String Cable Management - Below Ground",
  "Loose Cable Ends or Connectors",
  "MC4 Connector Support",
  "Cable Support with Clips/Ties",
  "Sharp Edge Exposure",
  "Cable String Numbering System",
  "Conduit Management with Foam Filling",
  "Cables Without End Caps",
  "Cable Specification",
  "Cable Installation Methodology",
  "Bi-Metallic Compatibility",
  "Cable Joints / Connectors / Lugs",
];

const PREDEFINED_MODULE_CABLE_TITLES = [
  "Module Installation per Manufacturer",
  "Structure Misalignment",
  "Mechanical Stress Concerns",
  "Cable Bending Radius",
  "Cable Tension",
  "Cable Installation",
  "Cable Support near MC4 Connectors",
  "Mechanical Cable Protection",
  "Cable String Numbering System",
];

const PREDEFINED_INVERTER_TITLES = [
  "Cooling Fans",
  "Error Alarms",
  "Display",
  "Filters",
  "Connections",
  "Corrosion",
  "Ventilation",
];

const PREDEFINED_GENERAL_TITLES = [
  "Overall Site Observations",
  "Corrective Actions Required",
  "Priority Assessment",
  "General Construction Issues",
];

const CATEGORY_OPTIONS = [
  { value: "civil", label: "Civil" },
  { value: "mechanical", label: "Mechanical" },
  { value: "electrical", label: "Electrical" },
  { value: "module_cable", label: "Module & Cable" },
  { value: "inverter", label: "Inverter" },
  { value: "general", label: "General" },
];

const STATUS_OPTIONS = [
  { value: "ok", label: "OK" },
  { value: "not-ok", label: "Not OK" },
  { value: "n-a", label: "N/A" },
  { value: "pending", label: "Pending" },
];

export const CombinedConstructionAspectsForm = memo(
  function ConstructionAspectsForm({
    siteId,
    templateId,
    reportId,
    projectId,
    reportTemplate,
    setSessionProgress,
    readOnly = false,
    previousCompletedReports = [],
  }: ConstructionAspectsFormProps) {
    const { notify } = useNotification();

    const { loading } = useAppSelector((state) => state?.files);
    const files = useVersionAwareFiles(readOnly);
    const [imageDescriptions, setImageDescriptions] =
      useImageDescriptions(files);
    const [formData, setFormData] = useState<ConstructionData>(initialData);
    const formDataRef = useLatest(formData);
    const [selectedImage, setSelectedImage] =
      useState<ConstructionImage | null>(null);
    const [imageToDelete, setImageToDelete] = useState<string | null>(null);
    const [uploadingImages, setUploadingImages] = useState<{
      [key: string]: boolean;
    }>({});

    const [observationData, setObservationData] = useState<
      Record<string, Partial<ObservationItem>>
    >({});

    const [observationProgressData, setObservationProgressData] = useState([]);
    const [uploadingObservationImages, setUploadingObservationImages] =
      useState<Record<string, boolean>>({});
    const [showCustomObservation, setShowCustomObservation] = useState<{
      [key: string]: boolean;
    }>({});
    const [expandedSections, setExpandedSections] = useState({
      civil: false,
      mechanical: false,
      electrical: false,
      moduleInstallation: false,
      inverter: false,
      observations: false,
      general: false,
    });

    const dispatch = useAppDispatch();

    // Load version data when viewing historical version
    const currentSiteData = useVersionAwareSiteData(readOnly);
    const currentObservationData = useVersionAwareObservations(readOnly);
    const inverterType = (currentSiteData?.projectData?.inverterType || "")
      .toString()
      .trim()
      .toLowerCase();
    const isStringInverter = inverterType === "string";

    // Autosave unsaved edits to IndexedDB so nothing is lost if the app
    // closes before the debounced server save fires (e.g. offline, tab
    // closed, phone loses power).
    const draftKey =
      !readOnly && reportId
        ? `report:${reportId}:operational-construction-aspects`
        : null;
    const { restoreDraft, clearSavedDraft } = useDraftAutosave(
      draftKey,
      formData,
    );
    const draftRestoredRef = useRef(false);

    // Restore any unsaved draft once on mount, before server data can
    // overwrite it below.
    useEffect(() => {
      if (!draftKey || draftRestoredRef.current) return;
      draftRestoredRef.current = true;
      restoreDraft().then((draft) => {
        if (draft?.data) {
          setFormData((prev) => ({ ...prev, ...draft.data }));
        }
      });
    }, [draftKey, restoreDraft]);

    // Once local form state matches the confirmed server record, the
    // IndexedDB draft is redundant — clear it so a future mount doesn't
    // restore stale local data over a fresher server record.
    useEffect(() => {
      if (!draftKey || !currentSiteData) return;
      if (JSON.stringify(currentSiteData) === JSON.stringify(formData)) {
        clearSavedDraft();
      }
    }, [draftKey, currentSiteData, formData, clearSavedDraft]);

    useEffect(() => {
      const overallProgress = operationalConstructionAspectsProgress(
        formData,
        observationProgressData,
        files,
      );

      setSessionProgress((prev) => ({
        ...prev,
        [templateId]: {
          ...prev[templateId],
          "construction-aspects-operational": overallProgress,
        },
      }));
    }, [
      formData,
      templateId,
      observationProgressData,
      files,
      setSessionProgress,
    ]);

    // Fetch data
    useEffect(() => {
      dispatch(
        fetchFiles({
          siteId,
          reportId,
        }),
      );
      dispatch(
        fetchObservations({
          siteId,
          reportId,
          templateId,
          // observationType: "construction",
        }),
      );
    }, [siteId, templateId, reportId, dispatch]);

    const handleSave = useCallback(
      async (data: ConstructionData) => {
        try {
          const mutationResult1 = await requireMutationSuccess(
            dispatch(
              updateSiteData({
                siteDataId: siteId,
                siteDataInfo: {
                  operationalData: {
                    ...currentSiteData?.operationalData,
                    constructionAspects: data,
                  },
                },
              }),
            ),
          );
          const config = notificationHelpers.formSaveSuccess("Site Data", [
            mutationResult1,
          ]);
          notify(config.message, config.type, config.options);
        } catch (error) {
          const config = notificationHelpers.formSaveError(
            "Site Data",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);

          throw error;
        }
      },
      [dispatch, siteId, currentSiteData?.operationalData, notify],
    );

    const {
      isSaving: isFormSaving,
      lastSaved: lastSavedForm,
      hasUnsavedChanges: hasFormUnsavedChanges,
      saveError: formSaveError,
      forceSave: forceFormSave,
      resetBaseline: resetFormBaseline,
    } = useDebouncedSave(formData, {
      delay: 7500,
      onSave: handleSave,
      onError: (error) => console.error("Save error:", error),
      readOnly,
    });

    useEffect(() => {
      if (currentSiteData) {
        const newFormData = {
          ...formDataRef.current,
          ...(currentSiteData?.operationalData?.constructionAspects || {}),
        };
        resetFormBaseline(newFormData);
        setFormData(newFormData);
        const obsItems =
          currentObservationData?.filter(
            (obs) => obs.observationType === "construction",
          ) || [];

        setObservationProgressData(obsItems);
      }
    }, [
      currentSiteData,
      currentObservationData,
      resetFormBaseline,
      formDataRef,
    ]);

    const handleSaveObservation = useCallback(
      async (data: typeof observationData) => {
        try {
          const importedObservationEntries = observationProgressData
            .filter(
              (observation: any) => observation?._sourceId && !observation?._id,
            )
            .map(
              (observation: any) =>
                [observation.id, observation] as [
                  string,
                  Partial<ObservationItem>,
                ],
            );
          const observationEntries = [
            ...Object.entries(data),
            ...importedObservationEntries.filter(
              ([observationId]) => !data[observationId],
            ),
          ];

          await Promise.all(
            observationEntries.map(([observationId, observationData]) => {
              const existingObservation = observationProgressData.find(
                (observation: any) =>
                  observation?._id === observationId ||
                  observation?.id === observationId,
              );
              const isOfflineCreatedObservation =
                Boolean(existingObservation?._offlineQueued) ||
                String(observationId).startsWith("offline");
              const isImportedObservation =
                String(observationId).startsWith("imported-") ||
                Boolean(existingObservation?._sourceId) ||
                (!existingObservation?._id && !isOfflineCreatedObservation);
                const {_id,existingObservationData}= existingObservation || {};
              const payload = {
                ...(existingObservationData || {}),
                ...observationData,
                siteId,
                reportId,
                templateId,
                observationType: "construction",
              };

              return dispatch(
                isImportedObservation
                  ? createObservation(payload)
                  : updateObservation({
                      observationId,
                      observationData: payload,
                    }),
              );
            }),
          );
          const config = notificationHelpers.formSaveSuccess("Observations");
          notify(config.message, config.type, config.options);
        } catch (error) {
          const config = notificationHelpers.formSaveError(
            "Observations",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
          throw error;
        }
      },
      [dispatch, notify, observationProgressData, reportId, siteId, templateId],
    );

    const observationSave = useDebouncedSave(observationData, {
      delay: 5000,
      onSave: handleSaveObservation,
      onError: (error) => console.error("Observation save error:", error),
      readOnly,
    });

    // Save handler for image descriptions
    const handleSaveImages = useCallback(
      async (descriptions: Record<string, string>) => {
        try {
          const mutationResult2 = await Promise.all(
            Object.entries(descriptions).map(([fileId, fileDescription]) =>
              requireMutationSuccess(
                dispatch(updateFile({ fileId, fileData: { fileDescription } })),
              ),
            ),
          );
          const config = notificationHelpers.formSaveSuccess(
            "Image Descriptions",
            [mutationResult2],
          );
          notify(config.message, config.type, config.options);
        } catch (error) {
          const config = notificationHelpers.formSaveError(
            "Image Descriptions",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);

          throw error;
        }
      },
      [dispatch, notify],
    );

    // Debounced save for image descriptions (5000ms delay)
    const {
      isSaving: isImageSaving,
      lastSaved: lastSavedImage,
      hasUnsavedChanges: hasImageUnsavedChanges,
      saveError: imageSaveError,
      forceSave: forceImageSave,
    } = useDebouncedSave(imageDescriptions, {
      delay: 5000,
      onSave: handleSaveImages,
      onError: (error) => console.error("Image description save error:", error),
      readOnly,
    });

    // Combine form and image save states
    const isSaving = isFormSaving || observationSave.isSaving || isImageSaving;
    const hasUnsavedChanges =
      hasFormUnsavedChanges ||
      observationSave.hasUnsavedChanges ||
      hasImageUnsavedChanges;
    const saveError =
      formSaveError || observationSave.saveError || imageSaveError;
    const lastSaved = useMemo(() => {
      const saveTimes = [
        lastSavedForm,
        observationSave.lastSaved,
        lastSavedImage,
      ].filter((date): date is Date => Boolean(date));
      return saveTimes.length
        ? new Date(Math.max(...saveTimes.map((date) => date.getTime())))
        : null;
    }, [lastSavedForm, lastSavedImage, observationSave.lastSaved]);

    const forceSave = useCallback(() => {
      forceFormSave();
      observationSave.forceSave();
      forceImageSave();
    }, [forceFormSave, forceImageSave, observationSave]);

    // Handle field changes
    const handleFieldChange = useCallback((field: keyof ConstructionData) => {
      return (value: string | boolean) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
      };
    }, []);

    // Handle checkbox changes
    const handleCheckboxChange = useCallback(
      (field: keyof ConstructionData, checked: boolean) => {
        console.log(field, checked);
        setFormData((prev) => ({ ...prev, [field]: checked }));
      },
      [],
    );

    // Toggle section expansion
    const toggleSection = useCallback(
      (section: keyof typeof expandedSections) => {
        setExpandedSections((prev) => ({
          ...prev,
          [section]: !prev[section],
        }));
      },
      [],
    );

    // Helper component for checkbox with comment
    const CheckboxWithComment = useCallback(
      ({
        id,
        label,
        checked,
        onChange,
        commentValue,
        onCommentChange,
        description,
        readOnly,
        disabled = false,
      }: {
        id: string;
        label: string;
        checked: boolean;
        onChange: (value: boolean) => void;
        commentValue: string;
        onCommentChange: (value: string) => void;
        description?: string;
        readOnly?: boolean;
        disabled?: boolean;
      }) => {
        return (
          <div className="space-y-2">
            <OptimizedCheckbox
              id={id}
              label={label}
              checked={checked}
              onChange={onChange}
              description={description}
              readOnly={readOnly}
              disabled={disabled}
            />
            <div className="ml-6 pl-4 border-l-2 border-gray-200 dark:border-border">
              <OptimizedTextarea
                id={`${id}-comment`}
                label="Comments"
                value={commentValue}
                onChange={onCommentChange}
                placeholder="Please provide additional details about this observation..."
                rows={2}
                readOnly={readOnly}
                disabled={disabled}
              />
            </div>
          </div>
        );
      },
      [],
    );

    // Positive Section Header
    const PositiveHeader = ({ title }: { title: string }) => (
      <div className="flex items-center gap-2 mb-3">
        <Check className="w-4 h-4 text-green-600" />
        <h4 className="text-sm font-medium text-green-700">{title}</h4>
      </div>
    );

    // Red Flag Section Header
    const RedFlagHeader = ({ title }: { title: string }) => (
      <div className="flex items-center gap-2 mb-3">
        <XCircle className="w-4 h-4 text-red-600" />
        <h4 className="text-sm font-medium text-red-700">{title}</h4>
      </div>
    );

    // Observation functions
    const getObservationTitleOptions = (category: string) => {
      let predefinedTitles: string[] = [];

      switch (category) {
        case "civil":
          predefinedTitles = PREDEFINED_CIVIL_TITLES;
          break;
        case "mechanical":
          predefinedTitles = PREDEFINED_MECHANICAL_TITLES;
          break;
        case "electrical":
          predefinedTitles = PREDEFINED_ELECTRICAL_TITLES;
          break;
        case "module_cable":
          predefinedTitles = PREDEFINED_MODULE_CABLE_TITLES;
          break;
        case "inverter":
          predefinedTitles = PREDEFINED_INVERTER_TITLES;
          break;
        case "general":
          predefinedTitles = PREDEFINED_GENERAL_TITLES;
          break;
        default:
          predefinedTitles = PREDEFINED_GENERAL_TITLES;
      }

      const predefinedOptions =
        predefinedTitles?.map((title) => ({
          value: title,
          label: title,
        })) ?? [];

      return predefinedOptions;
    };

    const toggleCustomObservation = (itemId: string) => {
      setShowCustomObservation((prev) => ({
        ...prev,
        [itemId]: !prev[itemId],
      }));
    };

    const addObservation = (category) => {
      requireMutationSuccess(
        dispatch(
          createObservation({
            siteId,
            reportId,
            templateId,
            observationType: "construction",
            category,
          }),
        ),
      )
        .then((mutationOutcome) => {
          const config = notificationHelpers.actionCreate(
            "Punch List Item",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          notify(errorMessage(error), "error", { title: "Changes not saved" });
        });
    };

    const updateObservationItem = (
      itemId: string,
      field: keyof ObservationItem,
      value: any,
    ) => {
      setObservationProgressData((prev) =>
        prev.map((observation) =>
          observation.id === itemId || observation._id === itemId
            ? {
                ...observation,
                [field]: value,
              }
            : observation,
        ),
      );
      setObservationData((prev) => ({
        ...prev,
        [itemId]: {
          ...prev[itemId],
          [field]: value,
        },
      }));
    };

    const getObservationValue = <K extends keyof ObservationItem>(
      observation: ObservationItem,
      field: K,
    ): ObservationItem[K] => {
      if (
        observationData[observation.id] &&
        field in observationData[observation.id]
      ) {
        return observationData[observation.id][field] as ObservationItem[K];
      }
      return observation[field];
    };

    const deleteObservationItem = (itemId: string) => {
      // Remove it from the section immediately. The thunk handles cancelling
      // an offline create/edit queue entry or queueing the server delete.
      setObservationProgressData((prev) =>
        prev.filter(
          (observation) =>
            observation.id !== itemId && observation._id !== itemId,
        ),
      );
      setObservationData((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });

      requireMutationSuccess(dispatch(deleteObservation(itemId)))
        .then((mutationOutcome) => {
          const config = notificationHelpers.actionDelete(
            "Punch List Item",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          notify(errorMessage(error), "error", { title: "Changes not saved" });
        });
    };

    // Image handling
    const handleImageUpload = useCallback(
      (files: FileList | File[] | null, filterData) => {
        if (!files) return;
        const fileArray = Array.from(files);

        const uploadKey = JSON.stringify(filterData);
        setUploadingImages((prev) => ({ ...prev, [uploadKey]: true }));
        requireMutationSuccess(
          dispatch(
            uploadMultipleFiles({
              files: fileArray,
              metadata: {
                siteId,
                entityType: `construction-aspects`,
                entityId: reportId,
                section: "construction-images",
                reportId,
              },
              filterData,
            }),
          ),
        )
          .then((mutationOutcome) => {
            fileArray.forEach((file) => {
              const config = notificationHelpers.imageUploadSuccess(
                file.name,
                mutationOutcome,
              );
              notify(config.message, config.type, config.options);
            });
          })
          .catch((error) => {
            const config = notificationHelpers.imageUploadError(
              fileArray.length === 1
                ? fileArray[0].name
                : `${fileArray.length} files`,
              errorMessage(error),
            );
            notify(config.message, config.type, config.options);
          })
          .finally(() => {
            setUploadingImages((prev) => ({ ...prev, [uploadKey]: false }));
          });
      },
      [siteId, reportId, dispatch, notify],
    );

    // Image handling for observations
    const handleObservationPhotoUpload = (
      itemId: string,
      files: FileList | File[] | null,
    ) => {
      if (!files) return;
      const fileArray = Array.from(files);

      setUploadingObservationImages((prev) => ({ ...prev, [itemId]: true }));

      requireMutationSuccess(
        dispatch(
          uploadMultipleFiles({
            files: fileArray,
            metadata: {
              siteId: siteId,
              entityType: "observation",
              entityId: itemId,
              section: "construction",
              reportId,
            },
          }),
        ),
      )
        .then((mutationOutcome) => {
          fileArray.forEach((file) => {
            const config = notificationHelpers.imageUploadSuccess(
              file.name,
              mutationOutcome,
            );
            notify(config.message, config.type, config.options);
          });
        })
        .catch((error) => {
          const config = notificationHelpers.imageUploadError(
            fileArray.length === 1
              ? fileArray[0].name
              : `${fileArray.length} files`,
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        })
        .finally(() => {
          setUploadingObservationImages((prev) => ({
            ...prev,
            [itemId]: false,
          }));
        });
    };

    const removeImage = (itemId: string, imageId: string) => {
      requireMutationSuccess(dispatch(deleteFile(imageId)))
        .then((mutationOutcome) => {
          const config = notificationHelpers.imageDeleteSuccess(
            "",
            mutationOutcome,
          );
          notify(config.message, config.type, config.options);
        })
        .catch((error) => {
          const config = notificationHelpers.imageDeleteError(
            "",
            errorMessage(error),
          );
          notify(config.message, config.type, config.options);
        });
    };

    const updateImageDescription = (imageId: string, description: string) => {
      setImageDescriptions((prev) => ({ ...prev, [imageId]: description }));
    };

    const handleRemoveImage = useCallback((imageId: string) => {
      setImageToDelete(imageId);
    }, []);

    const confirmRemoveImage = useCallback(() => {
      if (imageToDelete) {
        requireMutationSuccess(dispatch(deleteFile(imageToDelete)))
          .then((mutationOutcome) => {
            const config = notificationHelpers.imageDeleteSuccess(
              "Image",
              mutationOutcome,
            );
            notify(config.message, config.type, config.options);
          })
          .catch((error) => {
            const config = notificationHelpers.imageDeleteError(
              "Image",
              errorMessage(error),
            );
            notify(config.message, config.type, config.options);
          })
          .finally(() => {
            setImageToDelete(null);
          });
      }
    }, [imageToDelete, dispatch, notify]);

    const cancelRemoveImage = useCallback(() => {
      setImageToDelete(null);
    }, []);

    const formatFileSize = (bytes: number) => {
      if (bytes === 0) return "0 Bytes";
      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return (
        Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
      );
    };

    const getLoadingStatus = () => {
      if (saveError) return "error";
      if (isSaving) return "saving";
      if (hasUnsavedChanges) return "unsaved";
      if (lastSaved) return "saved";
      return "idle";
    };

    const isUploadingForSection = (filterData: any) => {
      const uploadKey = JSON.stringify(filterData);
      return uploadingImages[uploadKey] || false;
    };

    const getStatusColor = (status: string) => {
      switch (status) {
        case "ok":
          return "bg-green-100 text-green-800";
        case "not-ok":
          return "bg-red-100 text-red-800";
        case "n-a":
          return "bg-gray-100 text-gray-800";
        case "pending":
          return "bg-yellow-100 text-yellow-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    };

    const getCategoryIcon = (category: string) => {
      switch (category) {
        case "civil":
          return <Building className="w-4 h-4" />;
        case "mechanical":
          return <Wrench className="w-4 h-4" />;
        case "electrical":
          return <Zap className="w-4 h-4" />;
        case "module_cable":
          return <Cable className="w-4 h-4" />;
        case "inverter":
          return <Server className="w-4 h-4" />;
        default:
          return <HardHat className="w-4 h-4" />;
      }
    };

    const getCategoryColor = (category: string) => {
      switch (category) {
        case "civil":
          return "bg-blue-100 text-blue-700";
        case "mechanical":
          return "bg-orange-100 text-orange-700";
        case "electrical":
          return "bg-yellow-100 text-yellow-700";
        case "module_cable":
          return "bg-green-100 text-green-700";
        case "inverter":
          return "bg-purple-100 text-purple-700";
        default:
          return "bg-gray-100 text-gray-700";
      }
    };

    // Image Card Component
    const ImageCard = useStableMemoComponent<
      AuditImageCardProps<ConstructionImage>
    >(
      ({
        image,
        imageDescription,
        onView,
        onRemove,
        onDescriptionUpdate,
        isDeleting,
      }) => (
        <div key={image.id} className="relative group">
          <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden relative dark:bg-muted">
            <OfflineImage
              fileId={image.id}
              src={image?.fileUrl}
              alt="Construction aspect image"
              className="w-full h-full object-cover"
            />
            {loading && (isDeleting === true || isDeleting === image.id) && (
              <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
            )}
          </div>
          <div className="absolute top-2 right-2 flex gap-1">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
              onClick={() => onView(image)}
              disabled={loading}
              title="View Image"
            >
              <Eye className="w-3 h-3" />
            </Button>

            {!readOnly && (
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                onClick={() => onRemove(image.id)}
                disabled={loading}
                title="Delete Image"
              >
                <Trash2 className="w-3 h-3" />
              </Button>
            )}
          </div>
          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate truncate">
                {image?.originalName}
              </span>
              <Badge variant="outline" className="text-xs">
                {formatFileSize(image?.fileSize)}
              </Badge>
            </div>
            <OptimizedTextarea
              id={`desc-${image.id}`}
              label=""
              value={imageDescription}
              readOnly={readOnly}
              onChange={(value) => onDescriptionUpdate(image.id, value)}
              placeholder="Describe this image..."
              rows={2}
              disabled={loading}
            />
            <div className="text-xs text-slate-500 dark:text-muted-foreground">
              Date: {new Date(image.uploadDate).toLocaleDateString()}
            </div>
          </div>
        </div>
      ),
      (prevProps, nextProps) => {
        const propsEqual =
          prevProps.image.id === nextProps.image.id &&
          prevProps.imageDescription === nextProps.imageDescription &&
          prevProps.isDeleting === nextProps.isDeleting;
        return propsEqual;
      },
    );

    // Image Section Component
    const ImageSection = useStableMemoComponent<AuditImageSectionProps>(
      ({
        title,
        filterData,
        uploadFilterData,
        emptyMessage,
        buttonText = "Add Images",
      }) => {
        const sectionImages = files?.filter(
          (file) =>
            file?.relatedTo?.entityType === `construction-aspects` &&
            file?.relatedTo?.filterData &&
            JSON.stringify(file?.relatedTo?.filterData) ===
              JSON.stringify(filterData),
        );

        const isUploading = isUploadingForSection(uploadFilterData);

        return (
          <div className="space-y-4 pt-4 border-t">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">{title}</h4>
              {!readOnly && (
                <ImageCapturePicker
                  buttonText={buttonText}
                  isUploading={isUploading}
                  disabled={loading}
                  onConfirm={(selectedFiles) =>
                    handleImageUpload(selectedFiles, uploadFilterData)
                  }
                />
              )}
            </div>

            {isUploading && (
              <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
                <div className="text-center">
                  <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2 dark:text-muted-foreground" />
                  <p className="text-sm text-slate-600 dark:text-muted-foreground">Uploading images...</p>
                </div>
              </div>
            )}

            {!isUploading && sectionImages.length === 0 ? (
              <div className="flex items-center justify-between p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
                <div className="flex items-center gap-3">
                  <Camera className="w-5 h-5 text-slate-400 dark:text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-foreground">
                      No images added
                    </p>
                    <p className="text-xs text-slate-500 dark:text-muted-foreground">{emptyMessage}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sectionImages.map((image) => (
                  <ImageCard
                    key={image.id}
                    imageDescription={
                      imageDescriptions[image.id] ??
                      image?.fileDescription ??
                      ""
                    }
                    image={image}
                    onView={setSelectedImage}
                    onRemove={handleRemoveImage}
                    onDescriptionUpdate={updateImageDescription}
                    isDeleting={loading ? imageToDelete : false}
                  />
                ))}
              </div>
            )}
          </div>
        );
      },
    );

    // Render observation item
    const renderObservationItem = (observation, index) => {
      // Server observations use `_id`, while offline-created observations
      // use `_clientId`/`id`. Always use one stable identity for rendering,
      // editing, deleting, image association, and React keys.
      const observationId =
        observation?._id || observation?._clientId || observation?.id;
      const observationFiles = files?.filter(
        (file) => file?.relatedTo?.entityId === observationId,
      );
      const isUploadingObservationImage =
        uploadingObservationImages[observationId] || false;

      return (
        <div
          key={observationId}
          className="border rounded-lg p-4 mb-4 bg-white dark:bg-card"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Badge
                className={getCategoryColor(observation.category || "general")}
              >
                <span className="flex items-center gap-1">
                  {getCategoryIcon(observation.category || "general")}
                  {observation.category?.toUpperCase() || "GENERAL"}
                </span>
              </Badge>
              <span className="font-medium">
                #{index + 1}{" "}
                {observation.title || `Punch List Item ${index + 1}`}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={getStatusColor(observation.status)}>
                {observation?.status?.toUpperCase().replace("-", " ")}
              </Badge>
              {observationFiles.length > 0 && (
                <Badge variant="outline" className="text-xs">
                  <Camera className="w-3 h-3 mr-1" />
                  {observationFiles.length}
                </Badge>
              )}
              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-red-600 hover:text-red-700 h-6 w-6 p-0"
                  onClick={() => deleteObservationItem(observationId)}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <OptimizedSelect
                readOnly
                label="Category"
                id={`category-${observationId}`}
                value={
                  getObservationValue(observation, "category") || "general"
                }
                onChange={(value) =>
                  updateObservationItem(observationId, "category", value)
                }
                placeholder="Select category"
                options={CATEGORY_OPTIONS}
              />
            </div>

            <div className="space-y-2">
              <OptimizedSelect
                readOnly={readOnly}
                label="Punch List Item Title"
                id={`title-${observationId}`}
                value={getObservationValue(observation, "title") || ""}
                onChange={(value) => {
                  if (value === "custom") {
                    toggleCustomObservation(observationId);
                    updateObservationItem(observationId, "title", "");
                  } else if (value !== "select") {
                    updateObservationItem(observationId, "title", value);
                    setShowCustomObservation((prev) => ({
                      ...prev,
                      [observationId]: false,
                    }));
                  }
                }}
                placeholder="Select punch list item title"
                options={getObservationTitleOptions(
                  observation.category || "general",
                )}
                required
              />
              {showCustomObservation[observationId] && (
                <OptimizedInput
                  id={`custom-title-${observationId}`}
                  value={getObservationValue(observation, "title") || ""}
                  readOnly={readOnly}
                  onChange={(value) =>
                    updateObservationItem(observationId, "title", value)
                  }
                  placeholder="Enter custom punch list item title..."
                  required
                />
              )}
            </div>

            <div className="space-y-2">
              <OptimizedSelect
                readOnly={readOnly}
                label="Status"
                id={`status-${observationId}`}
                value={getObservationValue(observation, "status") || "pending"}
                onChange={(value) =>
                  updateObservationItem(observationId, "status", value)
                }
                placeholder="Select status"
                options={STATUS_OPTIONS}
                required
              />
            </div>

            <div className="space-y-2">
              <OptimizedTextarea
                label="Description"
                id={`description-${observationId}`}
                value={getObservationValue(observation, "description") || ""}
                readOnly={readOnly}
                onChange={(value) =>
                  updateObservationItem(observationId, "description", value)
                }
                placeholder="Describe the punch list item in detail..."
                rows={4}
                required
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">
                Photos <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                {!readOnly && (
                  <ImageCapturePicker
                    buttonText="Upload Photos"
                    isUploading={isUploadingObservationImage}
                    disabled={false}
                    onConfirm={(selectedFiles) =>
                      handleObservationPhotoUpload(observationId, selectedFiles)
                    }
                  />
                )}

                <span className="text-sm text-slate-500 dark:text-muted-foreground">
                  {observationFiles.length} photo
                  {observationFiles.length !== 1 ? "s" : ""} uploaded
                </span>
              </div>

              {observationFiles.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                  {observationFiles.map((image) => (
                    <div key={image.id} className="relative group">
                      <div className="aspect-video bg-slate-100 rounded overflow-hidden dark:bg-muted">
                        <OfflineImage
                          fileId={image.id}
                          src={image.fileUrl}
                          alt={`Observation ${index + 1} - Photo`}
                          className="w-full h-full object-cover rounded border"
                        />
                      </div>
                      <div className="absolute top-1 right-1 flex gap-1">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity
                              bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                          onClick={() => setSelectedImage(image)}
                          disabled={loading}
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                        {!readOnly && (
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity
                                bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center"
                            onClick={() => removeImage(observationId, image.id)}
                            disabled={loading}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>

                      <div className="mt-1 space-y-1">
                        <div className="flex items-center justify-between">
                          <div className="text-xs font-medium text-slate-700 truncate dark:text-foreground">
                            {image.originalName}
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {formatFileSize(image.fileSize)}
                          </Badge>
                        </div>
                        <textarea
                          value={
                            imageDescriptions[image.id] ??
                            image.fileDescription ??
                            ""
                          }
                          onChange={(e) =>
                            updateImageDescription(image.id, e.target.value)
                          }
                          placeholder="Describe this image..."
                          rows={1}
                          className="w-full p-1 text-xs border rounded resize-none"
                          disabled={loading}
                          readOnly={readOnly}
                        />
                        <div className="text-xs text-slate-500 dark:text-muted-foreground">
                          Date:{" "}
                          {new Date(image.uploadDate).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    };

    // Get stats for observations
    const getStatusStats = () => {
      const items =
        currentObservationData?.filter(
          (obs) => obs.observationType === "construction",
        ) || [];
      const ok = items.filter((item) => item.status === "ok").length;
      const notOk = items.filter((item) => item.status === "not-ok").length;
      const pending = items.filter((item) => item.status === "pending").length;
      const na = items.filter((item) => item.status === "n-a").length;
      return { ok, notOk, pending, na, total: items.length };
    };

    const stats = getStatusStats();
    const constructionObservations =
      observationProgressData?.filter(
        (obs) => obs.observationType === "construction",
      ) || [];

    return (
      <div className="space-y-4 md:space-y-6 relative">
        {!readOnly && (
          <LocalSectionImportControl
            previousCompletedReports={previousCompletedReports}
            sectionName="construction-aspects-operational"
            siteId={siteId}
            reportId={reportId}
            readOnly={readOnly}
            setFormData={setFormData}
            observationProgressData={observationProgressData}
            setObservationProgressData={setObservationProgressData}
          />
        )}

        {/* Floating save section - hidden when viewing read-only version */}
        {!readOnly && (
          <div className="fixed top-25 right-3 z-40 sm:right-4">
            <div className="flex flex-row items-center gap-2 sm:flex-col sm:items-end sm:gap-3">
              {/* Status indicator - same size as save button */}
              {false && (
                <div
                  className="
          bg-white/95 backdrop-blur-sm
          rounded-full border-2 border-gray-200/80
          shadow-lg
          w-12 h-12 sm:w-16 sm:h-16
          flex items-center justify-center
          group relative
          transition-all duration-300 ease-out
          transform hover:scale-110
          cursor-default
          p-2
        "
                >
                  <div className="flex flex-col items-center justify-center w-full h-full">
                    <span className="text-[10px] font-medium text-gray-600 leading-none mb-2 dark:text-muted-foreground">
                      Saved
                    </span>

                    <div className="flex items-center justify-center h-6">
                      <LoadingIndicator
                        status={getLoadingStatus()}
                        lastSaved={lastSaved}
                        error={saveError}
                      />
                    </div>
                  </div>

                  {/* Tooltip - Fixed with pointer-events-none */}
                  <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                    {getLoadingStatus() === "saving"
                      ? "Saving..."
                      : getLoadingStatus() === "saved"
                        ? `Saved ${lastSaved?.toLocaleTimeString()}`
                        : getLoadingStatus() === "error"
                          ? "Save failed"
                          : "Ready to save"}
                  </div>

                  {/* Error indicator */}
                  {getLoadingStatus() === "error" && (
                    <div className="absolute -top-1 -right-1">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                      <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                    </div>
                  )}
                </div>
              )}

              {/* Save button - Fixed with pointer-events-none */}
              <Button
                onClick={forceSave}
                disabled={isSaving || readOnly}
                className={`
          relative rounded-full w-12 h-12 sm:w-16 sm:h-16
          transition-all duration-300 ease-out
          flex flex-col items-center justify-center
          backdrop-blur-sm border-2
          shadow-lg hover:shadow-xl
          transform hover:scale-110 active:scale-95
          group
          ${
            hasUnsavedChanges && !readOnly
              ? "bg-gradient-to-br from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white border-red-300 dark:bg-gradient-to-br dark:border-red-300 dark:ring-red-300"
              : "bg-white/95 text-gray-600 border-gray-200/80 hover:bg-white/90 dark:bg-muted dark:text-muted-foreground dark:border-border dark:hover:bg-accent"
          }
          ${isSaving || readOnly ? "opacity-80 cursor-not-allowed" : "cursor-pointer"}
        `}
              >
                {isSaving ? (
                  <div className="flex flex-col items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mb-2"></div>
                    <span className="text-[10px] leading-none">Saving</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center">
                    <Save className="w-5 h-5 sm:mb-1" />
                    <span className="hidden text-[10px] font-medium leading-none sm:block">
                      Save
                    </span>
                  </div>
                )}

                {/* Unsaved changes indicator */}
                {hasUnsavedChanges && !isSaving && (
                  <div className="absolute -top-1 -right-1">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-ping"></div>
                    <div className="w-3 h-3 bg-red-500 rounded-full absolute top-0 left-0 border-2 border-white"></div>
                  </div>
                )}

                {/* Tooltip - Fixed with pointer-events-none */}
                <div className="absolute right-full mr-2 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                  {hasUnsavedChanges ? "Save changes" : "No changes to save"}
                </div>
              </Button>
            </div>
          </div>
        )}

        {/* ========== CIVIL SECTION ========== */}
        {currentSiteData?.installationType !== "Rooftop PV" &&
          templateId !== "operational" && (
            <Card>
              <CardHeader
                className="cursor-pointer"
                onClick={() => toggleSection("civil")}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Building className="w-6 h-6 text-blue-600" />
                    <CardTitle>Civil Aspects</CardTitle>
                  </div>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                    {expandedSections.civil ? <ChevronUp /> : <ChevronDown />}
                  </Button>
                </div>
              </CardHeader>
              {expandedSections.civil && (
                <CardContent className="space-y-4">
                  {/* Positive Observations */}
                  <div className="space-y-3">
                    <PositiveHeader title="Positive Observations" />
                    <div className="space-y-4">
                      <CheckboxWithComment
                        id="civil_separateOffloadingArea"
                        label="Is there a separate offloading/turning area suitable for construction vehicles?"
                        checked={formData.civil_separateOffloadingArea}
                        onChange={(checked) =>
                          handleCheckboxChange(
                            "civil_separateOffloadingArea",
                            checked,
                          )
                        }
                        commentValue={
                          formData.civil_separateOffloadingAreaComment
                        }
                        onCommentChange={handleFieldChange(
                          "civil_separateOffloadingAreaComment",
                        )}
                        readOnly={readOnly}
                      />

                      <CheckboxWithComment
                        id="civil_vehiclesSuitableForSite"
                        label="Are the vehicles and machinery suitable for the current site conditions?"
                        checked={formData.civil_vehiclesSuitableForSite}
                        onChange={(checked) =>
                          handleCheckboxChange(
                            "civil_vehiclesSuitableForSite",
                            checked,
                          )
                        }
                        commentValue={
                          formData.civil_vehiclesSuitableForSiteComment
                        }
                        onCommentChange={handleFieldChange(
                          "civil_vehiclesSuitableForSiteComment",
                        )}
                        readOnly={readOnly}
                      />
                    </div>
                  </div>

                  {/* Issues & Observations */}
                  <div className="space-y-3 pt-4 border-t">
                    <RedFlagHeader title="Issues & Observations" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <OptimizedTextarea
                        label="Condition of Site Access Road"
                        value={formData.civil_conditionOfSiteAccessRoad}
                        onChange={handleFieldChange(
                          "civil_conditionOfSiteAccessRoad",
                        )}
                        placeholder="Describe condition of access road..."
                        rows={3}
                        readOnly={readOnly}
                        required
                      />
                      <OptimizedTextarea
                        label="Condition of Construction Compound"
                        value={formData.civil_constructionCompoundCondition}
                        onChange={handleFieldChange(
                          "civil_constructionCompoundCondition",
                        )}
                        placeholder="Describe condition of construction compound..."
                        rows={3}
                        readOnly={readOnly}
                      />
                    </div>

                    <OptimizedTextarea
                      label="Traffic Problems for Local Residents"
                      value={formData.civil_trafficProblemsForResidents}
                      onChange={handleFieldChange(
                        "civil_trafficProblemsForResidents",
                      )}
                      placeholder="Describe any traffic problems caused for local residents..."
                      rows={3}
                      readOnly={readOnly}
                    />

                    <OptimizedTextarea
                      label="Onsite Traffic Management"
                      value={formData.civil_onsiteTrafficManagement}
                      onChange={handleFieldChange(
                        "civil_onsiteTrafficManagement",
                      )}
                      placeholder="Describe onsite traffic management..."
                      rows={3}
                      readOnly={readOnly}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <OptimizedTextarea
                        label="Condition of LV Trench"
                        value={formData.civil_lvTrenchCondition}
                        onChange={handleFieldChange("civil_lvTrenchCondition")}
                        placeholder="Describe LV trench condition..."
                        rows={4}
                        readOnly={readOnly}
                      />
                      <OptimizedTextarea
                        label="Condition of HV Trench"
                        value={formData.civil_hvTrenchCondition}
                        onChange={handleFieldChange("civil_hvTrenchCondition")}
                        placeholder="Describe HV trench condition..."
                        rows={4}
                        readOnly={readOnly}
                      />
                    </div>

                    <OptimizedTextarea
                      label="Ground Condition Problems During Construction"
                      value={formData.civil_groundConditionProblems}
                      onChange={handleFieldChange(
                        "civil_groundConditionProblems",
                      )}
                      placeholder="Describe any ground condition problems..."
                      rows={4}
                      readOnly={readOnly}
                    />

                    <OptimizedTextarea
                      label="Additional Civil Notes"
                      value={formData.civil_civilNotes}
                      onChange={handleFieldChange("civil_civilNotes")}
                      placeholder="Any additional civil observations..."
                      rows={3}
                      readOnly={readOnly}
                    />
                  </div>

                  <ImageSection
                    title="Civil Aspects Images"
                    filterData={{ civil_aspects: true }}
                    uploadFilterData={{ civil_aspects: true }}
                    emptyMessage="Add images related to civil aspects"
                    buttonText="Add Images"
                  />
                </CardContent>
              )}
            </Card>
          )}

        {/* ========== MECHANICAL SECTION ========== */}
        <Card >
          <CardHeader
            className="cursor-pointer"
            onClick={() => toggleSection("mechanical")}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Wrench className="w-6 h-6 text-orange-600" />
                <CardTitle>Mechanical Aspects</CardTitle>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                {expandedSections.mechanical ? <ChevronUp /> : <ChevronDown />}
              </Button>
            </div>
          </CardHeader>
          {expandedSections.mechanical && (
            <CardContent className="space-y-6">
              {/* Positive Observations */}
              <div className="space-y-3">
                <PositiveHeader title="Positive Observations" />
                <div className="space-y-4">
                  <CheckboxWithComment
                    id="mechanical_separateOffloadingArea"
                    label="Is there a separate offloading/turning area suitable for construction vehicles?"
                    checked={formData.mechanical_separateOffloadingArea}
                    onChange={(checked) =>
                      handleCheckboxChange(
                        "mechanical_separateOffloadingArea",
                        checked,
                      )
                    }
                    commentValue={
                      formData.mechanical_separateOffloadingAreaComment
                    }
                    onCommentChange={handleFieldChange(
                      "mechanical_separateOffloadingAreaComment",
                    )}
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="mechanical_vehiclesSuitableForSite"
                    label="Are the vehicles and machinery suitable for the current site conditions?"
                    checked={formData.mechanical_vehiclesSuitableForSite}
                    onChange={(checked) =>
                      handleCheckboxChange(
                        "mechanical_vehiclesSuitableForSite",
                        checked,
                      )
                    }
                    commentValue={
                      formData.mechanical_vehiclesSuitableForSiteComment
                    }
                    onCommentChange={handleFieldChange(
                      "mechanical_vehiclesSuitableForSiteComment",
                    )}
                    readOnly={readOnly}
                  />
                </div>
              </div>

              {/* Issues & Observations */}
              <div className="space-y-4 pt-4 border-t">
                <RedFlagHeader title="Framework - Quality of Piling/Ramming" />

                <OptimizedSelect
                  label="Quality of Piling/Ramming"
                  value={formData.mechanical_pilingQuality}
                  onChange={handleFieldChange("mechanical_pilingQuality")}
                  placeholder="Select piling quality"
                  options={pilingQualityOptions}
                  readOnly={readOnly}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <OptimizedTextarea
                    label="Ramming Head Profile Matching"
                    value={formData.mechanical_rammingHeadProfile}
                    onChange={handleFieldChange(
                      "mechanical_rammingHeadProfile",
                    )}
                    placeholder="Describe if ramming head matches post profile..."
                    rows={3}
                    readOnly={readOnly}
                  />
                  <OptimizedTextarea
                    label="Ramming Depth"
                    value={formData.mechanical_rammingDepth}
                    onChange={handleFieldChange("mechanical_rammingDepth")}
                    placeholder="Describe ramming depth..."
                    rows={3}
                    readOnly={readOnly}
                  />
                </div>

                <OptimizedTextarea
                  label="Any underground 'surprises' (stone, rock etc)?"
                  value={formData.mechanical_undergroundSurprises}
                  onChange={handleFieldChange(
                    "mechanical_undergroundSurprises",
                  )}
                  placeholder="Describe mechanical undergroundsurprises..."
                  rows={3}
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  label="Alignment / twisted?"
                  value={formData.mechanical_alignmentTwisted}
                  onChange={handleFieldChange("mechanical_alignmentTwisted")}
                  placeholder="Describe mechanical alignment Twisted..."
                  rows={3}
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  label="Method of re-galvanization onsite (if any)"
                  value={formData.mechanical_regalvanizationMethod}
                  onChange={handleFieldChange(
                    "mechanical_regalvanizationMethod",
                  )}
                  placeholder="Describe mechanical regalvanization method..."
                  rows={3}
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  label="Any drilling / concrete"
                  value={formData.mechanical_drillingConcrete}
                  onChange={handleFieldChange("mechanical_drillingConcrete")}
                  placeholder="Describe mechanical drilling concrete..."
                  rows={3}
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  label="Average Remaining Time"
                  value={formData.mechanical_averageRemainingTime}
                  onChange={handleFieldChange(
                    "mechanical_averageRemainingTime",
                  )}
                  placeholder="e.g., 2 weeks, 30 days..."
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  label="Mounting Angle Adjustment"
                  value={formData.mechanical_mountingAngleAdjustment}
                  onChange={handleFieldChange(
                    "mechanical_mountingAngleAdjustment",
                  )}
                  placeholder="Describe mounting angle adjustment..."
                  rows={3}
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  label="Onsite Traffic Management"
                  value={formData.mechanical_onsiteTrafficManagement}
                  onChange={handleFieldChange(
                    "mechanical_onsiteTrafficManagement",
                  )}
                  placeholder="Describe onsite traffic management..."
                  rows={3}
                  readOnly={readOnly}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <OptimizedTextarea
                    label="Condition of LV Trench"
                    value={formData.mechanical_lvTrenchCondition}
                    onChange={handleFieldChange("mechanical_lvTrenchCondition")}
                    placeholder="Describe LV trench condition..."
                    rows={4}
                    readOnly={readOnly}
                  />
                  <OptimizedTextarea
                    label="Condition of HV Trench"
                    value={formData.mechanical_hvTrenchCondition}
                    onChange={handleFieldChange("mechanical_hvTrenchCondition")}
                    placeholder="Describe HV trench condition..."
                    rows={4}
                    readOnly={readOnly}
                  />
                </div>

                <OptimizedTextarea
                  label="Ground Condition Problems During Construction"
                  value={formData.mechanical_groundConditionProblems}
                  onChange={handleFieldChange(
                    "mechanical_groundConditionProblems",
                  )}
                  placeholder="Describe any ground condition problems..."
                  rows={4}
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  label="Additional Mechanical Notes"
                  value={formData.mechanical_mechanicalNotes}
                  onChange={handleFieldChange("mechanical_mechanicalNotes")}
                  placeholder="Any additional mechanical observations..."
                  rows={3}
                  readOnly={readOnly}
                />
              </div>

              <ImageSection
                title="Mechanical Aspects Images"
                filterData={{ mechanical_aspects: true }}
                uploadFilterData={{ mechanical_aspects: true }}
                emptyMessage="Add images related to mechanical aspects"
                buttonText="Add Images"
              />
            </CardContent>
          )}
        </Card>

        {/* ========== ELECTRICAL SECTION ========== */}
        <Card>
          <CardHeader
            className="cursor-pointer"
            onClick={() => toggleSection("electrical")}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Zap className="w-6 h-6 text-yellow-600" />
                <CardTitle>Electrical Aspects</CardTitle>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                {expandedSections.electrical ? <ChevronUp /> : <ChevronDown />}
              </Button>
            </div>
          </CardHeader>
          {expandedSections.electrical && (
            <CardContent className="space-y-6">
              {/* Positive Observations */}
              <div className="space-y-3">
                <PositiveHeader title="Positive Observations" />
                <div className="space-y-4">
                  <OptimizedSelect
                    label="String Cable Type"
                    value={formData.electrical_stringCableType}
                    onChange={handleFieldChange("electrical_stringCableType")}
                    placeholder="Select cable type"
                    options={cableTypeOptions}
                    readOnly={readOnly}
                  />

                  {/* No Above Ground Cable */}
                  <CheckboxWithComment
                    id="electrical_noAboveGroundCable"
                    label="No above ground cable - All cables are installed below ground"
                    checked={formData.electrical_noAboveGroundCable}
                    onChange={(checked) =>
                      handleCheckboxChange(
                        "electrical_noAboveGroundCable",
                        checked,
                      )
                    }
                    commentValue={formData.electrical_noAboveGroundCableComment}
                    onCommentChange={handleFieldChange(
                      "electrical_noAboveGroundCableComment",
                    )}
                    readOnly={readOnly}
                  />

                  {/* No Below Ground Cable */}
                  <CheckboxWithComment
                    id="electrical_noBelowGroundCable"
                    label="No below ground cable - All cables are installed above ground"
                    checked={formData.electrical_noBelowGroundCable}
                    onChange={(checked) =>
                      handleCheckboxChange(
                        "electrical_noBelowGroundCable",
                        checked,
                      )
                    }
                    commentValue={formData.electrical_noBelowGroundCableComment}
                    onCommentChange={handleFieldChange(
                      "electrical_noBelowGroundCableComment",
                    )}
                    readOnly={readOnly}
                  />

                  {/* Above Ground - Only show if not "No above ground cable" */}
                  {!formData.electrical_noAboveGroundCable && (
                    <>
                      <h5 className="text-sm font-medium text-slate-600 dark:text-muted-foreground">
                        Above Ground
                      </h5>
                      <div className="grid grid-cols-1 gap-4">
                        <CheckboxWithComment
                          id="electrical_looseCableEnds"
                          label="Are all cable ends properly terminated and secured?"
                          checked={
                            formData.electrical_looseCableEnds ||
                            formData.electrical_stringAboveLooseCableConnector
                          }
                          onChange={(checked) => {
                            handleCheckboxChange(
                              "electrical_looseCableEnds",
                              checked,
                            );
                            handleCheckboxChange(
                              "electrical_stringAboveLooseCableConnector",
                              checked,
                            );
                          }}
                          commentValue={
                            formData.electrical_looseCableEndsComment ||
                            formData.electrical_stringAboveLooseCableConnectorComment
                          }
                          onCommentChange={(value) => {
                            handleFieldChange(
                              "electrical_looseCableEndsComment",
                            )(value);
                            handleFieldChange(
                              "electrical_stringAboveLooseCableConnectorComment",
                            )(value);
                          }}
                          readOnly={readOnly}
                        />

                        <CheckboxWithComment
                          id="electrical_mc4ConnectorSupport"
                          label="Are all MC4 connectors securely supported and protected?"
                          checked={
                            formData.electrical_mc4ConnectorSupport ||
                            formData.electrical_stringAboveProperSupport
                          }
                          onChange={(checked) => {
                            handleCheckboxChange(
                              "electrical_mc4ConnectorSupport",
                              checked,
                            );
                            handleCheckboxChange(
                              "electrical_stringAboveProperSupport",
                              checked,
                            );
                          }}
                          commentValue={
                            formData.electrical_mc4ConnectorSupportComment ||
                            formData.electrical_stringAboveProperSupportComment
                          }
                          onCommentChange={(value) => {
                            handleFieldChange(
                              "electrical_mc4ConnectorSupportComment",
                            )(value);
                            handleFieldChange(
                              "electrical_stringAboveProperSupportComment",
                            )(value);
                          }}
                          readOnly={readOnly}
                        />

                        <CheckboxWithComment
                          id="electrical_cableSupport"
                          label="Are cables properly supported using appropriate clips or cable ties?"
                          checked={formData.electrical_cableSupport}
                          onChange={(checked) =>
                            handleCheckboxChange(
                              "electrical_cableSupport",
                              checked,
                            )
                          }
                          commentValue={formData.electrical_cableSupportComment}
                          onCommentChange={handleFieldChange(
                            "electrical_cableSupportComment",
                          )}
                          readOnly={readOnly}
                        />

                        <CheckboxWithComment
                          id="electrical_sharpEdgeExposure"
                          label="Are all cables and components protected from sharp edges?"
                          checked={
                            formData.electrical_sharpEdgeExposure ||
                            formData.electrical_stringAboveNoSharpEdge
                          }
                          onChange={(checked) => {
                            handleCheckboxChange(
                              "electrical_sharpEdgeExposure",
                              checked,
                            );
                            handleCheckboxChange(
                              "electrical_stringAboveNoSharpEdge",
                              checked,
                            );
                          }}
                          commentValue={
                            formData.electrical_sharpEdgeExposureComment ||
                            formData.electrical_stringAboveNoSharpEdgeComment
                          }
                          onCommentChange={(value) => {
                            handleFieldChange(
                              "electrical_sharpEdgeExposureComment",
                            )(value);
                            handleFieldChange(
                              "electrical_stringAboveNoSharpEdgeComment",
                            )(value);
                          }}
                          readOnly={readOnly}
                        />

                        <CheckboxWithComment
                          id="electrical_cableStringNumbering"
                          label="Is the cable string numbering system correctly implemented and clearly identifiable?"
                          checked={
                            formData.electrical_cableStringNumbering ||
                            formData.electrical_stringAboveCableNumbering
                          }
                          onChange={(checked) => {
                            handleCheckboxChange(
                              "electrical_cableStringNumbering",
                              checked,
                            );
                            handleCheckboxChange(
                              "electrical_stringAboveCableNumbering",
                              checked,
                            );
                          }}
                          commentValue={
                            formData.electrical_cableStringNumberingComment ||
                            formData.electrical_stringAboveCableNumberingComment
                          }
                          onCommentChange={(value) => {
                            handleFieldChange(
                              "electrical_cableStringNumberingComment",
                            )(value);
                            handleFieldChange(
                              "electrical_stringAboveCableNumberingComment",
                            )(value);
                          }}
                          readOnly={readOnly}
                        />
                      </div>
                    </>
                  )}

                  {/* Below Ground - Only show if not "No below ground cable" */}
                  {!formData.electrical_noBelowGroundCable && (
                    <>
                      <h5 className="text-sm font-medium text-slate-600 pt-4 dark:text-muted-foreground">
                        Below Ground
                      </h5>
                      <div className="grid grid-cols-1 gap-4">
                        <CheckboxWithComment
                          id="electrical_conduitManagement"
                          label="Are cables correctly routed through conduit where required?"
                          checked={
                            formData.electrical_conduitManagement ||
                            formData.electrical_stringBelowRoutedThroughConduit
                          }
                          onChange={(checked) => {
                            handleCheckboxChange(
                              "electrical_conduitManagement",
                              checked,
                            );
                            handleCheckboxChange(
                              "electrical_stringBelowRoutedThroughConduit",
                              checked,
                            );
                          }}
                          commentValue={
                            formData.electrical_conduitManagementComment ||
                            formData.electrical_stringBelowRoutedThroughConduitComment
                          }
                          onCommentChange={(value) => {
                            handleFieldChange(
                              "electrical_conduitManagementComment",
                            )(value);
                            handleFieldChange(
                              "electrical_stringBelowRoutedThroughConduitComment",
                            )(value);
                          }}
                          readOnly={readOnly}
                        />

                        <CheckboxWithComment
                          id="electrical_conduitFilledWithFoam"
                          label="Are conduit entries adequately sealed with foam or an appropriate sealing material?"
                          checked={
                            formData.electrical_conduitFilledWithFoam ||
                            formData.electrical_stringBelowConduitSealedWithFoam
                          }
                          onChange={(checked) => {
                            handleCheckboxChange(
                              "electrical_conduitFilledWithFoam",
                              checked,
                            );
                            handleCheckboxChange(
                              "electrical_stringBelowConduitSealedWithFoam",
                              checked,
                            );
                          }}
                          commentValue={
                            formData.electrical_conduitFilledWithFoamComment ||
                            formData.electrical_stringBelowConduitSealedWithFoamComment
                          }
                          onCommentChange={(value) => {
                            handleFieldChange(
                              "electrical_conduitFilledWithFoamComment",
                            )(value);
                            handleFieldChange(
                              "electrical_stringBelowConduitSealedWithFoamComment",
                            )(value);
                          }}
                          readOnly={readOnly}
                        />
                      </div>
                    </>
                  )}

                  <h5 className="text-sm font-medium text-slate-600 pt-4 dark:text-muted-foreground">
                    DC Main Cable Management
                  </h5>
                  <div className="grid grid-cols-1 gap-4">
                    <CheckboxWithComment
                      id="electrical_cablesWithoutEndCap"
                      label="Are all cable terminations properly fitted with end caps?"
                      checked={
                        formData.electrical_cablesWithoutEndCap ||
                        formData.electrical_dcMainCablesWithoutEndCap
                      }
                      onChange={(checked) => {
                        handleCheckboxChange(
                          "electrical_cablesWithoutEndCap",
                          checked,
                        );
                        handleCheckboxChange(
                          "electrical_dcMainCablesWithoutEndCap",
                          checked,
                        );
                      }}
                      commentValue={
                        formData.electrical_cablesWithoutEndCapComment ||
                        formData.electrical_dcMainCablesWithoutEndCapComment
                      }
                      onCommentChange={(value) => {
                        handleFieldChange(
                          "electrical_cablesWithoutEndCapComment",
                        )(value);
                        handleFieldChange(
                          "electrical_dcMainCablesWithoutEndCapComment",
                        )(value);
                      }}
                      readOnly={readOnly}
                    />

                    <CheckboxWithComment
                      id="electrical_biMetallicCompatibility"
                      label="Are all bi-metallic connections compatible and free from corrosion risk?"
                      checked={
                        formData.electrical_biMetallicCompatibility ||
                        formData.electrical_dcMainBimetallicCompatibility
                      }
                      onChange={(checked) => {
                        handleCheckboxChange(
                          "electrical_biMetallicCompatibility",
                          checked,
                        );
                        handleCheckboxChange(
                          "electrical_dcMainBimetallicCompatibility",
                          checked,
                        );
                      }}
                      commentValue={
                        formData.electrical_biMetallicCompatibilityComment ||
                        formData.electrical_dcMainBimetallicCompatibilityComment
                      }
                      onCommentChange={(value) => {
                        handleFieldChange(
                          "electrical_biMetallicCompatibilityComment",
                        )(value);
                        handleFieldChange(
                          "electrical_dcMainBimetallicCompatibilityComment",
                        )(value);
                      }}
                      readOnly={readOnly}
                    />
                  </div>
                </div>
              </div>

              {/* Issues & Observations */}
              <div className="space-y-4 pt-4 border-t">
                <RedFlagHeader title="Cable Issues & Observations" />

                <OptimizedInput
                  label="Cable Specification"
                  value={
                    formData.electrical_cableSpecification ||
                    formData.electrical_dcMainCableSpecification
                  }
                  readOnly={readOnly}
                  onChange={(value) => {
                    handleFieldChange("electrical_cableSpecification")(value);
                    handleFieldChange("electrical_dcMainCableSpecification")(
                      value,
                    );
                  }}
                  placeholder="Enter cable specification details"
                />

                <OptimizedInput
                  label="Installation Methodology"
                  value={
                    formData.electrical_cableInstallationMethodology ||
                    formData.electrical_dcMainInstallationMethodology
                  }
                  readOnly={readOnly}
                  onChange={(value) => {
                    handleFieldChange(
                      "electrical_cableInstallationMethodology",
                    )(value);
                    handleFieldChange(
                      "electrical_dcMainInstallationMethodology",
                    )(value);
                  }}
                  placeholder="Describe installation methodology"
                />

                <OptimizedInput
                  label="Cable Joints / Connectors / Crimps / Dies"
                  value={
                    formData.electrical_cableConnectorsCrimpsDies ||
                    formData.electrical_dcMainCableJointsCrimpsDies
                  }
                  readOnly={readOnly}
                  onChange={(value) => {
                    handleFieldChange("electrical_cableConnectorsCrimpsDies")(
                      value,
                    );
                    handleFieldChange("electrical_dcMainCableJointsCrimpsDies")(
                      value,
                    );
                  }}
                  placeholder="Describe cable joints, connectors, crimps, dies"
                />

                <OptimizedTextarea
                  label="Additional Electrical Notes"
                  value={formData.electrical_electricalNotes}
                  onChange={handleFieldChange("electrical_electricalNotes")}
                  placeholder="Any additional electrical observations..."
                  rows={3}
                  readOnly={readOnly}
                />
              </div>

              <ImageSection
                title="Electrical Aspects Images"
                filterData={{ electrical_aspects: true }}
                uploadFilterData={{ electrical_aspects: true }}
                emptyMessage="Add images related to electrical aspects"
                buttonText="Add Images"
              />
            </CardContent>
          )}
        </Card>

        {/* ========== MODULE INSTALLATION & CABLE MANAGEMENT ========== */}
        <Card>
          <CardHeader
            className="cursor-pointer"
            onClick={() => toggleSection("moduleInstallation")}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Cable className="w-6 h-6 text-green-600" />
                <CardTitle>Module Installation & Cable Management</CardTitle>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                {expandedSections.moduleInstallation ? (
                  <ChevronUp />
                ) : (
                  <ChevronDown />
                )}
              </Button>
            </div>
          </CardHeader>
          {expandedSections.moduleInstallation && (
            <CardContent className="space-y-6">
              {/* Positive Observations */}
              <div className="space-y-3">
                <PositiveHeader title="Positive Observations" />
                <div className="space-y-4">
                  <CheckboxWithComment
                    id="moduleInstallationCompliant"
                    label="Is the module installation compliant with the manufacturer's recommendations?"
                    checked={formData.moduleInstallationCompliant}
                    onChange={(checked) =>
                      handleCheckboxChange(
                        "moduleInstallationCompliant",
                        checked,
                      )
                    }
                    commentValue={formData.moduleInstallationCompliantComment}
                    onCommentChange={handleFieldChange(
                      "moduleInstallationCompliantComment",
                    )}
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="moduleClampingCompliant"
                    label="Are module clamps correctly installed and securely fastened?"
                    checked={formData.moduleClampingCompliant}
                    onChange={(checked) =>
                      handleCheckboxChange("moduleClampingCompliant", checked)
                    }
                    commentValue={formData.moduleClampingCompliantComment}
                    onCommentChange={handleFieldChange(
                      "moduleClampingCompliantComment",
                    )}
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="structureAlignmentCompliant"
                    label="Is the mounting structure correctly aligned with no signs of misalignment?"
                    checked={formData.structureAlignmentCompliant}
                    onChange={(checked) =>
                      handleCheckboxChange(
                        "structureAlignmentCompliant",
                        checked,
                      )
                    }
                    commentValue={formData.structureAlignmentCompliantComment}
                    onCommentChange={handleFieldChange(
                      "structureAlignmentCompliantComment",
                    )}
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="cableBendingRadiusCompliant"
                    label="Is the minimum cable bending radius maintained throughout the installation?"
                    checked={formData.cableBendingRadiusCompliant}
                    onChange={(checked) =>
                      handleCheckboxChange(
                        "cableBendingRadiusCompliant",
                        checked,
                      )
                    }
                    commentValue={formData.cableBendingRadiusCompliantComment}
                    onCommentChange={handleFieldChange(
                      "cableBendingRadiusCompliantComment",
                    )}
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="cableTensionCompliant"
                    label="Is appropriate cable tension maintained without excessive strain?"
                    checked={formData.cableTensionCompliant}
                    onChange={(checked) =>
                      handleCheckboxChange("cableTensionCompliant", checked)
                    }
                    commentValue={formData.cableTensionCompliantComment}
                    onCommentChange={handleFieldChange(
                      "cableTensionCompliantComment",
                    )}
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="cableInstallationCompliant"
                    label="Cable routing and support installed correctly - Is the cable routing and installation compliant with design requirements?"
                    checked={formData.cableInstallationCompliant}
                    onChange={(checked) =>
                      handleCheckboxChange(
                        "cableInstallationCompliant",
                        checked,
                      )
                    }
                    commentValue={formData.cableInstallationCompliantComment}
                    onCommentChange={handleFieldChange(
                      "cableInstallationCompliantComment",
                    )}
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="cableSupportCompliant"
                    label="Are cables adequately supported using appropriate clips or cable ties?"
                    checked={formData.cableSupportCompliant}
                    onChange={(checked) =>
                      handleCheckboxChange("cableSupportCompliant", checked)
                    }
                    commentValue={formData.cableSupportCompliantComment}
                    onCommentChange={handleFieldChange(
                      "cableSupportCompliantComment",
                    )}
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="mc4ConnectorSupportCompliant"
                    label="Are all MC4 connectors securely supported and protected?"
                    checked={formData.mc4ConnectorSupportCompliant}
                    onChange={(checked) =>
                      handleCheckboxChange(
                        "mc4ConnectorSupportCompliant",
                        checked,
                      )
                    }
                    commentValue={formData.mc4ConnectorSupportCompliantComment}
                    onCommentChange={handleFieldChange(
                      "mc4ConnectorSupportCompliantComment",
                    )}
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="cableProtectionCompliant"
                    label="Do all cables have adequate mechanical protection?"
                    checked={formData.cableProtectionCompliant}
                    onChange={(checked) =>
                      handleCheckboxChange("cableProtectionCompliant", checked)
                    }
                    commentValue={formData.cableProtectionCompliantComment}
                    onCommentChange={handleFieldChange(
                      "cableProtectionCompliantComment",
                    )}
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="sharpEdgeProtectionCompliant"
                    label="Are cables adequately protected from contact with sharp edges?"
                    checked={formData.sharpEdgeProtectionCompliant}
                    onChange={(checked) =>
                      handleCheckboxChange(
                        "sharpEdgeProtectionCompliant",
                        checked,
                      )
                    }
                    commentValue={formData.sharpEdgeProtectionCompliantComment}
                    onCommentChange={handleFieldChange(
                      "sharpEdgeProtectionCompliantComment",
                    )}
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="cableNumberingCompliant"
                    label="Is the cable string numbering system correctly implemented and clearly identifiable?"
                    checked={formData.cableNumberingCompliant}
                    onChange={(checked) =>
                      handleCheckboxChange("cableNumberingCompliant", checked)
                    }
                    commentValue={formData.cableNumberingCompliantComment}
                    onCommentChange={handleFieldChange(
                      "cableNumberingCompliantComment",
                    )}
                    readOnly={readOnly}
                  />
                </div>
              </div>

              {/* Cable Issues */}
              <div className="space-y-3 pt-4 border-t">
                <RedFlagHeader title="Cable Issues" />
                <div className="space-y-4">
                  <CheckboxWithComment
                    id="cableIssuesRodentNests"
                    label="Are there any rodent nests or signs of rodent activity near cables?"
                    checked={formData.cableIssuesRodentNests}
                    onChange={(checked) =>
                      handleCheckboxChange("cableIssuesRodentNests", checked)
                    }
                    commentValue={formData.cableIssuesRodentNestsComment}
                    onCommentChange={handleFieldChange(
                      "cableIssuesRodentNestsComment",
                    )}
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="cableIssuesDamagedCable"
                    label="Are there any damaged cables visible (cuts, abrasions, or exposed conductors)?"
                    checked={formData.cableIssuesDamagedCable}
                    onChange={(checked) =>
                      handleCheckboxChange("cableIssuesDamagedCable", checked)
                    }
                    commentValue={formData.cableIssuesDamagedCableComment}
                    onCommentChange={handleFieldChange(
                      "cableIssuesDamagedCableComment",
                    )}
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="cableIssuesWrongCableSize"
                    label="Is the correct cable size being used for the application?"
                    checked={formData.cableIssuesWrongCableSize}
                    onChange={(checked) =>
                      handleCheckboxChange("cableIssuesWrongCableSize", checked)
                    }
                    commentValue={formData.cableIssuesWrongCableSizeComment}
                    onCommentChange={handleFieldChange(
                      "cableIssuesWrongCableSizeComment",
                    )}
                    readOnly={readOnly}
                  />

                  <CheckboxWithComment
                    id="cableIssuesTooManyRepairedJoints"
                    label="Are there excessive or poorly executed cable repairs/joints?"
                    checked={formData.cableIssuesTooManyRepairedJoints}
                    onChange={(checked) =>
                      handleCheckboxChange(
                        "cableIssuesTooManyRepairedJoints",
                        checked,
                      )
                    }
                    commentValue={
                      formData.cableIssuesTooManyRepairedJointsComment
                    }
                    onCommentChange={handleFieldChange(
                      "cableIssuesTooManyRepairedJointsComment",
                    )}
                    readOnly={readOnly}
                  />

                  <OptimizedTextarea
                    label="Additional Cable Issues Notes"
                    value={formData.cableIssuesAdditionalNotes}
                    onChange={handleFieldChange("cableIssuesAdditionalNotes")}
                    placeholder="Any additional cable-related issues or observations..."
                    rows={3}
                    readOnly={readOnly}
                  />
                </div>
              </div>

              {/* Issues & Observations */}
              <div className="space-y-4 pt-4 border-t">
                <RedFlagHeader title="Issues & Observations" />

                <OptimizedTextarea
                  label="Module Installation Notes"
                  value={formData.moduleInstallationNotes}
                  onChange={handleFieldChange("moduleInstallationNotes")}
                  placeholder="Record any observations about module installation..."
                  rows={3}
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  label="Module Clamping Notes"
                  value={formData.moduleClampingNotes}
                  onChange={handleFieldChange("moduleClampingNotes")}
                  placeholder="Record any observations about module clamping..."
                  rows={3}
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  label="Misalignment Details (if any)"
                  value={formData.structureMisalignmentDetails}
                  onChange={handleFieldChange("structureMisalignmentDetails")}
                  placeholder="Describe any structure misalignment observed..."
                  rows={3}
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  label="Mechanical Stress Concerns"
                  value={formData.mechanicalStressConcerns}
                  onChange={handleFieldChange("mechanicalStressConcerns")}
                  placeholder="Record any concerns about mechanical stress to table or module..."
                  rows={3}
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  label="Cable Management Notes"
                  value={formData.cableManagementNotes}
                  onChange={handleFieldChange("cableManagementNotes")}
                  placeholder="Record observations about cable management system..."
                  rows={3}
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  label="Cable Support Notes"
                  value={formData.cableSupportNotes}
                  onChange={handleFieldChange("cableSupportNotes")}
                  placeholder="Record observations about cable support system..."
                  rows={3}
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  label="Protection Issues Details"
                  value={formData.protectionIssuesDetails}
                  onChange={handleFieldChange("protectionIssuesDetails")}
                  placeholder="Describe any mechanical protection issues observed..."
                  rows={3}
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  label="Numbering System Description"
                  value={formData.numberingSystemDescription}
                  onChange={handleFieldChange("numberingSystemDescription")}
                  placeholder="Describe the cable string numbering system in place..."
                  rows={3}
                  readOnly={readOnly}
                />

                <OptimizedTextarea
                  label="Numbering Issues Details"
                  value={formData.numberingIssuesDetails}
                  onChange={handleFieldChange("numberingIssuesDetails")}
                  placeholder="Describe any issues with the cable string numbering..."
                  rows={3}
                  readOnly={readOnly}
                />
              </div>

              <ImageSection
                title="Module & Cable Management Images"
                filterData={{ module_cable_management: true }}
                uploadFilterData={{ module_cable_management: true }}
                emptyMessage="Add images to document module and cable management"
                buttonText="Add Images"
              />
            </CardContent>
          )}
        </Card>

        {/* ========== INVERTER SECTION ========== */}
        <Card>
          <CardHeader
            className="cursor-pointer"
            onClick={() => toggleSection("inverter")}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Server className="w-6 h-6 text-purple-600" />
                <CardTitle>Inverter Inspection</CardTitle>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                {expandedSections.inverter ? <ChevronUp /> : <ChevronDown />}
              </Button>
            </div>
          </CardHeader>
          {expandedSections.inverter && (
            <CardContent className="space-y-6">
              {isStringInverter ? (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
                  Inverter inspection is not applicable for string inverter
                  sites.
                </div>
              ) : (
                <>
                  {/* Positive Observations */}
                  <div className="space-y-3">
                    <PositiveHeader title="Positive Observations" />
                    <div className="space-y-4">
                      <CheckboxWithComment
                        id="inverterCoolingFans"
                        label="Are the cooling fans operating correctly and free from obstruction?"
                        checked={formData.inverterCoolingFans}
                        onChange={(checked) =>
                          handleCheckboxChange("inverterCoolingFans", checked)
                        }
                        commentValue={formData.inverterCoolingFansComment}
                        onCommentChange={handleFieldChange(
                          "inverterCoolingFansComment",
                        )}
                        readOnly={readOnly}
                      />

                      <CheckboxWithComment
                        id="inverterErrorAlarms"
                        label="Are there any active error alarms or fault indicators on the display?"
                        checked={formData.inverterErrorAlarms}
                        onChange={(checked) =>
                          handleCheckboxChange("inverterErrorAlarms", checked)
                        }
                        commentValue={formData.inverterErrorAlarmsComment}
                        onCommentChange={handleFieldChange(
                          "inverterErrorAlarmsComment",
                        )}
                        readOnly={readOnly}
                      />

                      <CheckboxWithComment
                        id="inverterDisplay"
                        label="Is the display functioning properly and showing correct information?"
                        checked={formData.inverterDisplay}
                        onChange={(checked) =>
                          handleCheckboxChange("inverterDisplay", checked)
                        }
                        commentValue={formData.inverterDisplayComment}
                        onCommentChange={handleFieldChange(
                          "inverterDisplayComment",
                        )}
                        readOnly={readOnly}
                      />

                      <CheckboxWithComment
                        id="inverterFilters"
                        label="Are the air filters clean and properly installed?"
                        checked={formData.inverterFilters}
                        onChange={(checked) =>
                          handleCheckboxChange("inverterFilters", checked)
                        }
                        commentValue={formData.inverterFiltersComment}
                        onCommentChange={handleFieldChange(
                          "inverterFiltersComment",
                        )}
                        readOnly={readOnly}
                      />

                      <CheckboxWithComment
                        id="inverterConnections"
                        label="Are all electrical connections secure and free from signs of overheating?"
                        checked={formData.inverterConnections}
                        onChange={(checked) =>
                          handleCheckboxChange("inverterConnections", checked)
                        }
                        commentValue={formData.inverterConnectionsComment}
                        onCommentChange={handleFieldChange(
                          "inverterConnectionsComment",
                        )}
                        readOnly={readOnly}
                      />

                      <CheckboxWithComment
                        id="inverterCorrosion"
                        label="Is there any visible corrosion on the inverter casing or components?"
                        checked={formData.inverterCorrosion}
                        onChange={(checked) =>
                          handleCheckboxChange("inverterCorrosion", checked)
                        }
                        commentValue={formData.inverterCorrosionComment}
                        onCommentChange={handleFieldChange(
                          "inverterCorrosionComment",
                        )}
                        readOnly={readOnly}
                      />

                      <CheckboxWithComment
                        id="inverterVentilation"
                        label="Is the ventilation system adequate and free from blockages?"
                        checked={formData.inverterVentilation}
                        onChange={(checked) =>
                          handleCheckboxChange("inverterVentilation", checked)
                        }
                        commentValue={formData.inverterVentilationComment}
                        onCommentChange={handleFieldChange(
                          "inverterVentilationComment",
                        )}
                        readOnly={readOnly}
                      />
                    </div>
                  </div>

                  {/* Issues & Observations */}
                  <div className="space-y-4 pt-4 border-t">
                    <RedFlagHeader title="Issues & Observations" />

                    <OptimizedTextarea
                      label="Additional Inverter Notes"
                      value={formData.inverterAdditionalNotes}
                      onChange={handleFieldChange("inverterAdditionalNotes")}
                      placeholder="Any additional inverter-related observations or issues..."
                      rows={4}
                      readOnly={readOnly}
                    />
                  </div>

                  <ImageSection
                    title="Inverter Images"
                    filterData={{ inverter_aspects: true }}
                    uploadFilterData={{ inverter_aspects: true }}
                    emptyMessage="Add images related to inverter inspection"
                    buttonText="Add Images"
                  />
                </>
              )}
            </CardContent>
          )}
        </Card>

        {/* ========== PUNCH LIST / OBSERVATIONS SECTION ========== */}
        <Card>
          <CardHeader
            className="cursor-pointer"
            onClick={() => toggleSection("observations")}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-purple-600" />
                <CardTitle>Punch List / Observations</CardTitle>
              </div>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                {expandedSections.observations ? (
                  <ChevronUp />
                ) : (
                  <ChevronDown />
                )}
              </Button>
            </div>
          </CardHeader>
          {expandedSections.observations && (
            <CardContent>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm text-slate-500 mt-1 dark:text-muted-foreground">
                    Add specific punch list items with photos
                  </p>
                </div>
                {!readOnly && constructionObservations?.length > 0 && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => addObservation("mechanical")}
                      className="gap-2"
                      variant="outline"
                      size="sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add Mechanical
                    </Button>
                    <Button
                      onClick={() => addObservation("electrical")}
                      className="gap-2"
                      variant="outline"
                      size="sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add Electrical
                    </Button>
                    <Button
                      onClick={() => addObservation("civil")}
                      className="gap-2"
                      variant="outline"
                      size="sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add Civil
                    </Button>
                    {/* <Button
                      onClick={() => addObservation("inverter")}
                      className="gap-2"
                      variant="outline"
                      size="sm"
                    >
                      <Plus className="w-4 h-4" />
                      Add Inverter
                    </Button> */}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {stats.total}
                  </div>
                  <div className="text-sm text-slate-600 dark:text-muted-foreground">Total Items</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {stats.ok}
                  </div>
                  <div className="text-sm text-slate-600 dark:text-muted-foreground">OK</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {stats.notOk}
                  </div>
                  <div className="text-sm text-slate-600 dark:text-muted-foreground">Issues</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">
                    {stats.pending}
                  </div>
                  <div className="text-sm text-slate-600 dark:text-muted-foreground">Pending</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600">
                    {stats.na}
                  </div>
                  <div className="text-sm text-slate-600 dark:text-muted-foreground">N/A</div>
                </div>
              </div>

              {constructionObservations.length === 0 ? (
                <div className="text-center py-8">
                  <HardHat className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 dark:text-muted-foreground">
                    No punch list items added yet
                  </p>
                  <p className="text-sm text-slate-500 mb-4 dark:text-muted-foreground">
                    Click a button below to start recording punch list items
                  </p>
                  {!readOnly && (
                    <div className="flex flex-wrap justify-center gap-2">
                      <Button
                        onClick={() => addObservation("mechanical")}
                        className="gap-2"
                        variant="outline"
                        size="sm"
                      >
                        <Plus className="w-4 h-4" />
                        Add Mechanical
                      </Button>
                      <Button
                        onClick={() => addObservation("electrical")}
                        className="gap-2"
                        variant="outline"
                        size="sm"
                      >
                        <Plus className="w-4 h-4" />
                        Add Electrical
                      </Button>
                      <Button
                        onClick={() => addObservation("civil")}
                        className="gap-2"
                        variant="outline"
                        size="sm"
                      >
                        <Plus className="w-4 h-4" />
                        Add Civil
                      </Button>
                      {/* <Button
                        onClick={() => addObservation("inverter")}
                        className="gap-2"
                        variant="outline"
                        size="sm"
                      >
                        <Plus className="w-4 h-4" />
                        Add Inverter
                      </Button> */}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {constructionObservations.map((observation, index) =>
                    renderObservationItem(observation, index),
                  )}
                </div>
              )}

              {/* Punch List Summary */}
              <div className="mt-6 pt-6 border-t">
                <div className="space-y-2">
                  <OptimizedTextarea
                    label="Punch List Summary"
                    value={formData.punchListSummary}
                    onChange={(val) =>
                      setFormData((prev) => ({
                        ...prev,
                        punchListSummary: val,
                      }))
                    }
                    placeholder="Provide an overall summary of all punch list items..."
                    rows={4}
                    className="w-full p-3 border rounded-md resize-none"
                    readOnly={readOnly}
                  />
                </div>
              </div>
            </CardContent>
          )}
        </Card>

        {/* Construction Images Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Construction Images
              </CardTitle>
              {!readOnly && (
                <ImageCapturePicker
                  buttonText="Add Images"
                  isUploading={isUploadingForSection({
                    construction_main: true,
                  })}
                  disabled={loading}
                  onConfirm={(selectedFiles) =>
                    handleImageUpload(selectedFiles, {
                      construction_main: true,
                    })
                  }
                />
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {isUploadingForSection({ construction_main: true }) && (
              <div className="flex items-center justify-center py-6 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
                <div className="text-center">
                  <Loader2 className="w-6 h-6 text-slate-400 animate-spin mx-auto mb-2 dark:text-muted-foreground" />
                  <p className="text-sm text-slate-600 dark:text-muted-foreground">
                    Uploading construction images...
                  </p>
                </div>
              </div>
            )}

            {!isUploadingForSection({ construction_main: true }) &&
            files?.filter(
              (file) =>
                file?.relatedTo?.entityType === `construction-aspects` &&
                file?.relatedTo?.entityId === reportId &&
                file?.relatedTo?.filterData?.construction_main,
            ).length === 0 ? (
              <div className="flex items-center justify-between p-4 border border-dashed border-slate-300 rounded-lg bg-slate-50 dark:bg-muted dark:border-border">
                <div className="flex items-center gap-3">
                  <Camera className="w-5 h-5 text-slate-400 dark:text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-slate-700 dark:text-foreground">
                      No construction images yet
                    </p>
                    <p className="text-xs text-slate-500 dark:text-muted-foreground">
                      Add images to document construction aspects
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {files
                  ?.filter(
                    (file) =>
                      file?.relatedTo?.entityType === `construction-aspects` &&
                      file?.relatedTo?.entityId === reportId &&
                      file?.relatedTo?.filterData?.construction_main,
                  )
                  .map((image) => (
                    <ImageCard
                      key={image.id}
                      imageDescription={
                        imageDescriptions[image.id] ??
                        image?.fileDescription ??
                        ""
                      }
                      image={image}
                      onView={setSelectedImage}
                      onRemove={handleRemoveImage}
                      onDescriptionUpdate={updateImageDescription}
                      isDeleting={loading ? imageToDelete : false}
                    />
                  ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Delete Confirmation Modal */}
        {imageToDelete && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-card rounded-lg max-w-md w-full p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="w-6 h-6 text-red-500" />
                <h3 className="text-lg font-semibold">Delete Image</h3>
              </div>
              <p className="text-slate-600 dark:text-muted-foreground mb-6">
                Are you sure you want to delete this image? This action cannot
                be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={cancelRemoveImage}
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={confirmRemoveImage}
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Image Preview Modal */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-card rounded-lg max-w-4xl max-h-full overflow-auto">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="text-lg font-semibold">
                  {selectedImage.originalName}
                </h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedImage(null)}
                  disabled={loading}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div className="p-4">
                <OfflineImage
                  fileId={selectedImage.id}
                  src={selectedImage?.fileUrl}
                  alt={selectedImage.originalName}
                  className="max-w-full max-h-96 mx-auto"
                />
                {selectedImage.fileDescription && (
                  <div className="mt-4 p-3 bg-slate-50 dark:bg-muted rounded">
                    <p className="text-sm text-slate-700 dark:text-muted-foreground">
                      {selectedImage.fileDescription}
                    </p>
                  </div>
                )}
                <div className="mt-2 text-sm text-slate-500 dark:text-muted-foreground">
                  Size: {formatFileSize(selectedImage.fileSize)} • Date:{" "}
                  {new Date(selectedImage.uploadDate).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Progress Footer */}
        <div className="sticky bottom-2 md:bottom-4 z-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-white dark:bg-card rounded-lg border shadow-lg backdrop-blur-sm bg-white/95 dark:bg-surface-raised/95">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full sm:w-auto">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-slate-700 dark:text-muted-foreground">
                Progress:
              </span>
              <Badge variant="outline">
                {operationalConstructionAspectsProgress(
                  formData,
                  observationProgressData,
                  files,
                )}
                %
              </Badge>
            </div>
          </div>
          <div className="text-xs md:text-sm text-slate-500 dark:text-muted-foreground hidden lg:block">
            Auto-save enabled • Changes saved automatically
          </div>
        </div>
      </div>
    );
  },
);
